/**
 * Navbar Component with Hacker Executable Badges, Tree Submenus & Compact Auth Icons
 */

import { soundFx } from './SoundFx.js';
import { authModal } from './AuthModal.js';
import { authService } from '../services/authService.js';
import { getCategories, getFileSections } from '../services/api.js';
import { getLanguage, localize, setLanguage } from '../services/i18n.js';
const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

export class Navbar {
  constructor(onNavigate, onToggleMatrix) {
    this.onNavigate = onNavigate;
    this.onToggleMatrix = onToggleMatrix;
    this.currentRoute = 'home';
    this.categories = [];
    this.fileSections = [];
    void getCategories().then(items => { this.categories = items; this.render(); }).catch(() => {});
    void getFileSections().then(items => { this.fileSections = items; this.render(); }).catch(() => {});
    window.addEventListener('fixlan:categories_changed', () => {
      void getCategories().then(items => { this.categories = items; this.render(); }).catch(() => {});
    });
    window.addEventListener('fixlan:language_change', () => {
      void getCategories().then(items => { this.categories = items; this.render(); }).catch(() => {});
      void getFileSections().then(items => { this.fileSections = items; this.render(); }).catch(() => {});
    });
    window.addEventListener('fixlan:file_sections_changed', () => {
      void getFileSections().then(items => { this.fileSections = items; this.render(); }).catch(() => {});
    });
    this.currentCategory = null;
    this.isMobileMenuOpen = false;
    this.isUserDropdownOpen = false;
    this.isLanguageDropdownOpen = false;

    // Listen to authentication changes
    window.addEventListener('fixlan:auth_change', () => {
      this.isUserDropdownOpen = false;
      this.isLanguageDropdownOpen = false;
      this.render();
    });

    // Close header dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      const closeUser = this.isUserDropdownOpen && !e.target.closest('.user-profile-menu');
      const closeLanguage = this.isLanguageDropdownOpen && !e.target.closest('.language-menu');
      if (closeUser || closeLanguage) {
        if (closeUser) this.isUserDropdownOpen = false;
        if (closeLanguage) this.isLanguageDropdownOpen = false;
        this.render();
      }
    });
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || (!this.isMobileMenuOpen && !this.isUserDropdownOpen && !this.isLanguageDropdownOpen)) return;
      const focusTarget = this.isLanguageDropdownOpen ? '#btn-language-menu' : this.isMobileMenuOpen ? '#btn-mobile-menu' : '#btn-user-badge';
      this.isMobileMenuOpen = this.isUserDropdownOpen = this.isLanguageDropdownOpen = false;
      this.render();
      document.querySelector(focusTarget)?.focus();
    });
  }

  setRoute(route, category = null) {
    this.currentRoute = route;
    this.currentCategory = category;
    this.isMobileMenuOpen = false;
    this.isUserDropdownOpen = false;
    this.isLanguageDropdownOpen = false;
    this.render();
  }

  fileMenuItems() {
    const byParent = new Map(), visited = new Set();
    for (const section of this.fileSections) {
      const parent = section.parentId || null; if (!byParent.has(parent)) byParent.set(parent, []); byParent.get(parent).push(section);
    }
    const items = [];
    const walk = (parent, depth) => {
      for (const section of byParent.get(parent) || []) {
        if (visited.has(section.id)) continue; visited.add(section.id);
        const slug = escapeHtml(section.slug), name = escapeHtml(section.name);
        items.push(`<li class="dropdown-item"><a href="#files/section/${slug}" data-route="files" data-category="section/${slug}"><span class="tree-symbol">${'│ '.repeat(Math.min(depth, 4))}├──</span> ${name}</a></li>`);
        walk(section.id, depth + 1);
      }
    };
    walk(null, 0); return items.join('');
  }

  render() {
    const headerEl = document.querySelector('.site-header') || document.createElement('header');
    headerEl.className = 'site-header';

    const isHome = this.currentRoute === 'home';
    const isBlog = this.currentRoute === 'blog';
    const isFiles = this.currentRoute === 'files';
    const isGallery = this.currentRoute === 'gallery';
    const isAbout = this.currentRoute === 'about';

    const rawUser = authService.getCurrentUser();
    const currentUser = rawUser ? Object.fromEntries(Object.entries(rawUser).map(([key, value]) => [key, escapeHtml(value)])) : null;

    headerEl.innerHTML = `
      <div class="container header-container">
        <!-- Brand Prompt -->
        <a href="#home" class="brand-logo" id="nav-brand">
          <span class="brand-prompt">&gt;_</span>
          <span>fixlan.ru</span><span class="brand-cursor"></span>
        </a>

        <!-- Desktop Navigation & Inline Auth Icons (One Row) -->
        <nav aria-label="Основная навигация" style="display: flex; align-items: center; gap: 8px;">
          <ul id="site-navigation" class="nav-menu ${this.isMobileMenuOpen ? 'is-open' : ''}">
            <!-- 1. Home -->
            <li class="nav-item">
              <a href="#home" class="nav-link ${isHome ? 'active' : ''}" data-route="home">
                ./home.exe
              </a>
            </li>

            <!-- 2. Blog with Submenu -->
            <li class="nav-item">
              <a href="#blog" class="nav-link ${isBlog ? 'active' : ''}" data-route="blog">
                ./blog.sh <span class="dropdown-arrow">▼</span>
              </a>
              <ul class="dropdown-menu">
                <li class="dropdown-header">// Блог категории</li>
                <li class="dropdown-item"><a href="#blog" data-route="blog">Все статьи</a></li>
${this.categories.map(category => `<li class="dropdown-item"><a href="#blog/${encodeURIComponent(category.slug)}" data-route="blog" data-category="${escapeHtml(category.slug)}">${escapeHtml(category.name)}</a></li>`).join('')}
              </ul>
            </li>

            <!-- 3. Files with Submenu -->
            <li class="nav-item">
              <a href="#files" class="nav-link ${isFiles ? 'active' : ''}" data-route="files">
                ./files.bin <span class="dropdown-arrow">▼</span>
              </a>
              <ul class="dropdown-menu">
                <li class="dropdown-header">// Разделы файлов</li>
                <li class="dropdown-item">
                  <a href="#files" data-route="files" data-category="all">
                    <span class="tree-symbol">├──</span> Все файлы
                  </a>
                </li>
${this.fileMenuItems()}
              </ul>
            </li>

            <!-- 4. Gallery -->
            <li class="nav-item">
              <a href="#gallery" class="nav-link ${isGallery ? 'active' : ''}" data-route="gallery">
                ./gallery.raw
              </a>
            </li>

            <!-- 5. About Us -->
            <li class="nav-item">
              <a href="#about" class="nav-link ${isAbout ? 'active' : ''}" data-route="about">
                ./about.md
              </a>
            </li>

            <!-- Auth Compact Icons Placed in Same Line with Menu -->
            <li class="nav-item language-menu">
              <button type="button" class="language-trigger" id="btn-language-menu" aria-label="Открыть выбор языка" aria-expanded="${this.isLanguageDropdownOpen}" aria-controls="language-dropdown">${getLanguage().toUpperCase()}</button>
              <div class="language-dropdown ${this.isLanguageDropdownOpen ? 'is-open' : ''}" id="language-dropdown" role="menu" aria-label="Выбор языка">
                <button type="button" class="language-option ${getLanguage() === 'ru' ? 'active' : ''}" data-language="ru" role="menuitemradio" aria-checked="${getLanguage() === 'ru'}"><span>RU</span><small>Русский</small></button>
                <button type="button" class="language-option ${getLanguage() === 'en' ? 'active' : ''}" data-language="en" role="menuitemradio" aria-checked="${getLanguage() === 'en'}"><span>EN</span><small>English</small></button>
              </div>
            </li>
            <li class="nav-item" style="display: inline-flex; align-items: center;">
              <div class="header-auth">
                ${!currentUser ? `
                  <!-- Compact Login Icon Button -->
                  <button class="auth-icon-btn auth-icon-login" id="btn-header-login" title="Вход в аккаунт (./login.sh)" aria-label="Вход">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                      <polyline points="10 17 15 12 10 7"></polyline>
                      <line x1="15" y1="12" x2="3" y2="12"></line>
                    </svg>
                  </button>

                  <!-- Compact Register Icon Button -->
                  <button class="auth-icon-btn auth-icon-register" id="btn-header-register" title="Регистрация (./register.bin)" aria-label="Регистрация">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <line x1="19" y1="8" x2="19" y2="14"></line>
                      <line x1="22" y1="11" x2="16" y2="11"></line>
                    </svg>
                  </button>
                ` : `
                  <!-- Compact User Session Badge Button -->
                  <div class="user-profile-menu">
                    <button class="user-icon-btn" id="btn-user-badge" aria-expanded="${this.isUserDropdownOpen}" aria-controls="user-dropdown" title="${currentUser.username}@fixlan [${currentUser.role}]">
                      <span class="user-status-dot"></span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>${currentUser.username}@fixlan</span>
                      <span style="font-size: 0.65rem; color: var(--text-dim);">▼</span>
                    </button>

                    <!-- Dropdown Details -->
                    <div class="user-dropdown ${this.isUserDropdownOpen ? 'is-open' : ''}" id="user-dropdown">
                      <div class="user-dropdown-header">
                        <div class="user-dropdown-title">// ТЕКУЩАЯ СЕССИЯ</div>
                        <div class="user-dropdown-name">${currentUser.username}@fixlan</div>
                      </div>
                      <div class="user-dropdown-info">
                        <div><span>UID:</span> <span style="color: var(--accent-green);">${currentUser.uid}</span></div>
                        <div><span>Email:</span> <span style="color: var(--accent-green);">${currentUser.email}</span></div>
                        <div><span>Подтверждение email:</span> <span>${currentUser.emailVerifiedAt ? 'Подтверждён' : 'Не подтверждён'}</span></div>
                        ${currentUser.emailVerifiedAt ? '' : '<a href="#resend-verification" data-route="resend-verification" class="profile-link">Подтвердить email</a>'}
                        <a href="#change-password" data-route="change-password" class="profile-link">Сменить пароль</a>
                        ${currentUser.role === 'SYSADMIN' ? '' : '<a href="#security" data-route="security" class="profile-link">Безопасность аккаунта</a>'}
                        <div><span>Status:</span> <span style="color: var(--neon-green);">AUTHORIZED</span></div>
                        <div><span>Role:</span> <span style="color: var(--neon-yellow);">${currentUser.role}</span></div>
                      </div>
                      ${(currentUser.role === 'SYSADMIN') ? `
                      <a href="#admin" class="user-dropdown-admin" id="btn-header-admin" data-route="admin" style="display: block; margin-top: 10px; padding: 6px; background: rgba(0,255,65,0.1); border: 1px dashed var(--neon-green); color: var(--neon-green); text-align: center; text-decoration: none; font-size: 0.8rem; cursor: pointer;">
                        <span>⚙️</span> ./admin.exe (SOC)
                      </a>
                      ` : ''}
                      <button class="user-dropdown-logout" id="btn-header-logout" style="margin-top: 10px;">
                        <span>🚪</span> Завершить сессию (Выход)
                      </button>
                    </div>
                  </div>
                `}
              </div>
            </li>
          </ul>

          <!-- Mobile Hamburger -->
          <button class="mobile-toggle" id="btn-mobile-menu" aria-label="Меню" aria-expanded="${this.isMobileMenuOpen}" aria-controls="site-navigation">
            ${this.isMobileMenuOpen ? '✕' : '☰'}
          </button>
        </nav>
      </div>
    `;
    headerEl.querySelectorAll('.nav-link.active').forEach(link => link.setAttribute('aria-current', 'page'));

    const state = authService.getState();
    if (state.status === 'loading' || state.status === 'error') {
      const auth = headerEl.querySelector('.header-auth');
      const notice = document.createElement('button'); notice.className = 'filter-btn';
      notice.textContent = state.status === 'loading' ? 'Проверка сессии…' : 'Повторить проверку сессии';
      notice.disabled = state.status === 'loading'; notice.title = state.error;
      notice.addEventListener('click', () => authService.refresh()); auth.replaceChildren(notice);
    }
    this.bindEvents(headerEl);
    return localize(headerEl);
  }

  bindEvents(headerEl) {
    const languageMenu = headerEl.querySelector('#btn-language-menu');
    languageMenu?.addEventListener('click', event => {
      event.stopPropagation();
      soundFx.playKeyClick();
      this.isLanguageDropdownOpen = !this.isLanguageDropdownOpen;
      this.isUserDropdownOpen = false;
      this.render();
      if (this.isLanguageDropdownOpen) document.querySelector('.language-option.active')?.focus();
    });
    headerEl.querySelectorAll('[data-language]').forEach(button => button.addEventListener('click', () => {
      const language = button.getAttribute('data-language');
      this.isLanguageDropdownOpen = false;
      if (language !== getLanguage()) setLanguage(language); else this.render();
    }));
    // Navigation routing click delegation
    headerEl.querySelectorAll('a[data-route]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        soundFx.playKeyClick();
        const route = link.getAttribute('data-route');
        const category = link.getAttribute('data-category') || null;
        this.isMobileMenuOpen = false;
        this.onNavigate(route, category);
      });
    });

    const brand = headerEl.querySelector('#nav-brand');
    if (brand) {
      brand.addEventListener('click', (e) => {
        e.preventDefault();
        soundFx.playKeyClick();
        this.isMobileMenuOpen = false;
        this.onNavigate('home', null);
      });
    }

    // Auth Login & Register Open Modal
    const loginBtn = headerEl.querySelector('#btn-header-login');
    if (loginBtn) {
      loginBtn.addEventListener('click', () => {
        soundFx.playKeyClick();
        authModal.open('login');
      });
    }

    const regBtn = headerEl.querySelector('#btn-header-register');
    if (regBtn) {
      regBtn.addEventListener('click', () => {
        soundFx.playKeyClick();
        authModal.open('register');
      });
    }

    // User Profile Dropdown
    const userBadge = headerEl.querySelector('#btn-user-badge');
    if (userBadge) {
      userBadge.addEventListener('click', (e) => {
        e.stopPropagation();
        soundFx.playKeyClick();
        this.isUserDropdownOpen = !this.isUserDropdownOpen;
        this.render();
      });
    }

    const logoutBtn = headerEl.querySelector('#btn-header-logout');
    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => { logoutBtn.disabled = true; try { await authService.logout(); } catch (error) { logoutBtn.textContent = error.message + ' Повторить выход'; logoutBtn.disabled = false; } });
    }

    // Mobile Hamburger
    const mobileBtn = headerEl.querySelector('#btn-mobile-menu');
    if (mobileBtn) {
      mobileBtn.addEventListener('click', () => {
        soundFx.playKeyClick();
        this.isMobileMenuOpen = !this.isMobileMenuOpen;
        this.render();
      });
    }
  }
}
